export const GRAFTCODE_ONBOARDING_PHASE = {
  idle: 'idle',
  wizard: 'wizard',
} as const

export type GraftcodeOnboardingPhase = (typeof GRAFTCODE_ONBOARDING_PHASE)[keyof typeof GRAFTCODE_ONBOARDING_PHASE]

export const GRAFTCODE_ONBOARDING_WIZARD_STEP = {
  goal: 'goal',
  scenario: 'scenario',
} as const

export type GraftcodeOnboardingWizardStep =
  (typeof GRAFTCODE_ONBOARDING_WIZARD_STEP)[keyof typeof GRAFTCODE_ONBOARDING_WIZARD_STEP]

export const GRAFTCODE_ONBOARDING_GOAL = {
  ai: 'ai',
  integrations: 'integrations',
} as const

export type GraftcodeOnboardingGoalId = (typeof GRAFTCODE_ONBOARDING_GOAL)[keyof typeof GRAFTCODE_ONBOARDING_GOAL]

export const GRAFTCODE_ONBOARDING_SETUP_MODE = {
  agent: 'agent',
  manual: 'manual',
} as const

export type GraftcodeOnboardingSetupMode =
  (typeof GRAFTCODE_ONBOARDING_SETUP_MODE)[keyof typeof GRAFTCODE_ONBOARDING_SETUP_MODE]

export const GRAFTCODE_ONBOARDING_SCENARIO = {
  frontend: 'frontend',
  backend: 'backend',
  microservices: 'microservices',
  modules: 'modules',
  mcp: 'mcp',
  monolith: 'monolith',
} as const

export type GraftcodeOnboardingScenarioId =
  (typeof GRAFTCODE_ONBOARDING_SCENARIO)[keyof typeof GRAFTCODE_ONBOARDING_SCENARIO]

export const GRAFTCODE_ONBOARDING_ASSETS_BASE = '/assets/graftcode-onboarding'

export const GRAFTCODE_ONBOARDING_CONTENT_BASE = '/generated/onboarding-content'

export const GRAFTCODE_ONBOARDING_GOALS = [
  {
    id: GRAFTCODE_ONBOARDING_GOAL.ai,
    titleKey: 'goals.ai.title',
    icon: `${GRAFTCODE_ONBOARDING_ASSETS_BASE}/goal-ai.svg`,
    pointKeys: ['goals.ai.points.0', 'goals.ai.points.1', 'goals.ai.points.2'] as const,
    setupMode: GRAFTCODE_ONBOARDING_SETUP_MODE.agent,
  },
  {
    id: GRAFTCODE_ONBOARDING_GOAL.integrations,
    titleKey: 'goals.integrations.title',
    icon: `${GRAFTCODE_ONBOARDING_ASSETS_BASE}/goal-integrations.svg`,
    pointKeys: ['goals.integrations.points.0', 'goals.integrations.points.1', 'goals.integrations.points.2'] as const,
    setupMode: GRAFTCODE_ONBOARDING_SETUP_MODE.manual,
  },
] as const

export const GRAFTCODE_ONBOARDING_SCENARIOS = [
  {
    id: GRAFTCODE_ONBOARDING_SCENARIO.frontend,
    labelKey: 'scenarios.frontend.label',
    hintKey: 'scenarios.frontend.hint',
    icon: `${GRAFTCODE_ONBOARDING_ASSETS_BASE}/scenario-frontend.svg`,
    contentDir: '1-connect-frontend-to-backend',
    defaultStack: 'react',
  },
  {
    id: GRAFTCODE_ONBOARDING_SCENARIO.backend,
    labelKey: 'scenarios.backend.label',
    hintKey: 'scenarios.backend.hint',
    icon: `${GRAFTCODE_ONBOARDING_ASSETS_BASE}/scenario-backend.svg`,
    contentDir: '2-expose-backend',
    defaultStack: 'javascript',
  },
  {
    id: GRAFTCODE_ONBOARDING_SCENARIO.microservices,
    labelKey: 'scenarios.microservices.label',
    hintKey: 'scenarios.microservices.hint',
    icon: `${GRAFTCODE_ONBOARDING_ASSETS_BASE}/scenario-microservices.svg`,
    contentDir: '3-connect-microservices',
    defaultStack: 'javascript',
  },
  {
    id: GRAFTCODE_ONBOARDING_SCENARIO.modules,
    labelKey: 'scenarios.modules.label',
    hintKey: 'scenarios.modules.hint',
    icon: `${GRAFTCODE_ONBOARDING_ASSETS_BASE}/scenario-modules.svg`,
    contentDir: '4-use-modules-from-any-technology',
    defaultStack: 'javascript',
  },
  {
    id: GRAFTCODE_ONBOARDING_SCENARIO.mcp,
    labelKey: 'scenarios.mcp.label',
    hintKey: 'scenarios.mcp.hint',
    icon: `${GRAFTCODE_ONBOARDING_ASSETS_BASE}/scenario-mcp.svg`,
    contentDir: '5-expose-mcp',
    defaultStack: 'javascript',
  },
  {
    id: GRAFTCODE_ONBOARDING_SCENARIO.monolith,
    labelKey: 'scenarios.monolith.label',
    hintKey: 'scenarios.monolith.hint',
    icon: `${GRAFTCODE_ONBOARDING_ASSETS_BASE}/scenario-monolith.svg`,
    contentDir: '6-switch-between-monolith-and-microservices',
    defaultStack: 'javascript',
  },
] as const

export function resolveSetupModeForGoal(goalId: GraftcodeOnboardingGoalId): GraftcodeOnboardingSetupMode {
  const goal = GRAFTCODE_ONBOARDING_GOALS.find((item) => item.id === goalId)
  return goal?.setupMode ?? GRAFTCODE_ONBOARDING_SETUP_MODE.manual
}

export function resolveScenarioContentPath(
  scenarioId: GraftcodeOnboardingScenarioId,
  stack?: string,
  setupMode: GraftcodeOnboardingSetupMode = GRAFTCODE_ONBOARDING_SETUP_MODE.manual
): string {
  const scenario = GRAFTCODE_ONBOARDING_SCENARIOS.find((item) => item.id === scenarioId)
  if (!scenario) {
    return ''
  }
  const file = stack || scenario.defaultStack
  const setupFolder =
    setupMode === GRAFTCODE_ONBOARDING_SETUP_MODE.agent
      ? GRAFTCODE_ONBOARDING_SETUP_MODE.agent
      : GRAFTCODE_ONBOARDING_SETUP_MODE.manual
  return `${GRAFTCODE_ONBOARDING_CONTENT_BASE}/${scenario.contentDir}/${setupFolder}/${file}.md`
}
